import React, { useState, useRef, useEffect, use } from 'react';
import { Upload as UploadIcon, X, CheckCircle, AlertCircle, FileCheck, Cloud } from 'lucide-react';
import api from '../../axios/api';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { showNotification } from "../../redux/slices/notificationSlice";
import { baseURL } from '../../axios/api';

const CloudinaryFileUploader = ({
  multiple = true,
  onUploadComplete,
  existingFiles = [],
  allowedTypes = '*'
}) => {
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState({});
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState(
    existingFiles?.some(file => file.name && file.path && file.filename)
      ? existingFiles
      : []
  );

  // const [uploadedFiles, setUploadedFiles] = useState(existingFiles);
  
  const fileInputRef = useRef(null);
  const cancelTokenSourceRef = useRef(null);
  const dispatch = useDispatch();

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('files', file);

    cancelTokenSourceRef.current = axios.CancelToken.source();

    try {
      const response = await api.post('common/uploadCloudinary', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        cancelToken: cancelTokenSourceRef.current.token,
        onUploadProgress: (progressEvent) => {
          const progressPercent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(prev => ({ ...prev, [file.name]: progressPercent }));
        },
      });
      if (response.data.status === '200') {
        const uploadedFile = {
          name: response.data.Uploads[0].originalName,
          path: response.data.Uploads[0].path,
          size: response.data.Uploads[0].size,
          filename: response.data.Uploads[0].filename
        };

        setUploadedFiles(prev => [...prev, uploadedFile]);
        dispatch(showNotification({
          message: `${file.name} uploaded successfully`,
          type: "success"
        }));

        onUploadComplete?.(uploadedFile);
        setProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
        return true;
      }
      return false;
    } catch (err) {
      if (axios.isCancel(err)) {
        setError('Upload cancelled');
      } else {
        dispatch(showNotification({
          message: `Failed to upload ${file.name}`,
          type: "error"
        }));
        setError(err.response?.data?.message || 'Upload failed');
      }
      setProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[file.name];
        return newProgress;
      });
      return false;
    }
  };

  const onFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      setFiles(selectedFiles);
      setStatus('uploading');
      setError('');

      for (const file of selectedFiles) {
        await uploadFile(file);
      }

      setStatus('idle');
      setFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeUploadedFile = (fileToRemove) => {
    setUploadedFiles(prev => prev.filter(file => file.path !== fileToRemove.path));
    onUploadComplete?.(uploadedFiles.filter(file => file.path !== fileToRemove.path));
  };

  // Show upload area only if multiple is true or no files are uploaded
  const showUploadArea = multiple || uploadedFiles.length === 0;

  return (
    <div className="w-full max-w-md p-6 border rounded-lg shadow-sm">
      <div className="space-y-4">
        {showUploadArea ? (
          <div className="flex items-center justify-center w-full">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <UploadIcon className="w-8 h-8 mb-4 text-gray-500" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  Upload {multiple ? 'up to 10 files' : 'a file'} (10MB MAX)
                </p>
                {allowedTypes !== '*' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Allowed types: {allowedTypes}
                  </p>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={onFileSelect}
                disabled={status === 'uploading'}
                multiple={multiple}
                accept={allowedTypes}
              />
            </label>
          </div>
        ) : (<div className="flex items-center justify-center w-full">
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-green-100">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <FileCheck className="w-8 h-8 mb-4 text-gray-500" />
              <p className="mb-2 text-sm text-gray-500">
                <span className="font-semibold">Upload Complete</span>
              </p>
            </div>
          </label>
        </div>)}

        {/* Upload Progress */}
        {Object.keys(progress).length > 0 && (
          <div className="mt-4 space-y-2">
            {Object.entries(progress).map(([fileName, progressValue]) => (
              <div key={fileName} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {fileName}
                  </span>
                  <span className="text-sm text-gray-500">{progressValue}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-blue-600"
                    style={{ width: `${progressValue}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Uploaded Files List */}
        {uploadedFiles.length > 0 && (
          <div className="mt-4 space-y-2">
            <h3 className="text-sm font-medium text-gray-700">Uploaded Files</h3>
            {uploadedFiles.map((file, index) => (
              <> <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                <a
                  href={`${file.path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline truncate flex-1"
                >
                  {file.name || file.filename}
                </a>
                <button
                  onClick={() => removeUploadedFile(file)}
                  className="p-1 hover:bg-gray-200 rounded-full ml-2"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
                {file?.size > 0 && (<div className="p-2">File Size: {(file.size / (1024 * 1024)).toFixed(2)} MB</div>)}
                <div className='p-2'>Max Size: 10MB</div></>
            ))}
          </div>
        )}

        {error && (
          <div className="flex items-center text-sm text-red-600">
            <AlertCircle className="w-4 h-4 mr-1" />
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default CloudinaryFileUploader;